/**
 * A FeedbackApi-shaped object that queues method calls until the real
 * widget bundle finishes loading, then drains the queue against the real
 * implementation.
 *
 * Why we need this: hosts that already have code like
 *   const fb = createFeedback({...})
 *   fb.identify({id, email, name})
 *   useEffect(() => fb.open(...), [])
 * shouldn't have to refactor for async. The loader's createFeedback
 * returns sync — a deferred handle that records intent, then replays it
 * once the real bundle arrives. Idiomatic queue-then-flush pattern.
 *
 * If the bundle never loads (network failure, SRI mismatch, kill switch),
 * `_fail()` is called instead. Void methods (identify/setMetadata/open/...)
 * silently drop in that case — the widget just doesn't appear. The
 * `submit` Promise rejects so callers can surface real errors.
 */

import type {
  FeedbackApi,
  ReportPayload,
  SubmittedReport,
  UserIdentity,
} from '../types'

type VoidMethod = 'show' | 'hide' | 'shutdown'
type ArgMethod = 'open' | 'identify' | 'setMetadata'

type Queued =
  | { kind: 'void'; method: VoidMethod }
  | { kind: 'arg'; method: 'open'; arg: Parameters<FeedbackApi['open']>[0] }
  | { kind: 'arg'; method: 'identify'; arg: UserIdentity }
  | { kind: 'arg'; method: 'setMetadata'; arg: Record<string, unknown> }
  | {
      kind: 'submit'
      payload: Partial<ReportPayload> & { description: string }
      resolve: (r: SubmittedReport) => void
      reject: (e: unknown) => void
    }

export interface DeferredHandle extends FeedbackApi {
  /** Called by the loader once the bundle is loaded and a real API exists. */
  _attach(real: FeedbackApi): void
  /** Called by the loader if the bundle fails to load. */
  _fail(err: Error): void
}

export function createDeferredApi(): DeferredHandle {
  let real: FeedbackApi | null = null
  let failure: Error | null = null
  const queue: Queued[] = []

  function flush(item: Queued): void {
    if (real) {
      if (item.kind === 'void') {
        ;(real[item.method] as () => void)()
      } else if (item.kind === 'arg') {
        ;(real[item.method] as (a: unknown) => void)(item.arg)
      } else {
        // submit
        Promise.resolve(real.submit(item.payload)).then(
          item.resolve,
          item.reject,
        )
      }
      return
    }
    if (failure) {
      if (item.kind === 'submit') item.reject(failure)
      // Void / arg methods silently dropped — the widget never appeared.
      return
    }
    queue.push(item)
  }

  return {
    show() {
      flush({ kind: 'void', method: 'show' })
    },
    hide() {
      flush({ kind: 'void', method: 'hide' })
    },
    shutdown() {
      flush({ kind: 'void', method: 'shutdown' })
    },
    open(arg) {
      flush({ kind: 'arg', method: 'open', arg })
    },
    identify(arg) {
      flush({ kind: 'arg', method: 'identify', arg })
    },
    setMetadata(arg) {
      flush({ kind: 'arg', method: 'setMetadata', arg })
    },
    submit(payload) {
      return new Promise<SubmittedReport>((resolve, reject) => {
        flush({ kind: 'submit', payload, resolve, reject })
      })
    },
    _attach(impl) {
      real = impl
      while (queue.length > 0) flush(queue.shift()!)
    },
    _fail(err) {
      failure = err
      // Drain any pending submits with the failure; drop void/arg.
      for (const item of queue) {
        if (item.kind === 'submit') item.reject(err)
      }
      queue.length = 0
    },
  }
}
